import React, { useMemo } from 'react';
import { useCurrencySymbol } from '../../../../../utils/currencyUtils';
import {
    parseAmountNumber,
    formatAmountComma,
} from '../../../../../utils/decimalAmountUtils';
import {
  canonicalInstallationAssociatedTaskDescription,
  isTrainingExpenseDisplayRow,
  sumInstallationAssociatedTrainingValues,
} from '../../../../../utils/trainingOtherExpenseSync';

const normalizeId = (id) => {
    if (id == null || id === "") return "";
    if (typeof id === "object") {
        return String(id._id ?? id.id ?? "").trim();
    }
    return String(id).trim();
};

const SoftwareTypeServiceTable = ({ matchedProjects, filteredQuotation, currentPerson, tasks, AssociatedRef }) => {

    const currency = useCurrencySymbol();
    const formatMoneyComma = formatAmountComma;

    const softwarePersons = useMemo(
        () => (Array.isArray(currentPerson) ? currentPerson : []),
        [currentPerson]
    );

    const serviceTypeKey = String(softwarePersons[0]?.typeService || "")
        .trim()
        .toLowerCase();

    // Same project-id universe AllServiceTable uses for Associated Cost totals
    const serviceProjectIds = useMemo(() => {
        const ids = new Set();
        softwarePersons.forEach((p) => {
            const pid = normalizeId(p?.ProjectID);
            if (pid) ids.add(pid);
        });
        Object.values(filteredQuotation || {}).forEach((typeServices) => {
            Object.entries(typeServices || {}).forEach(([ts, items]) => {
                if (
                    serviceTypeKey &&
                    String(ts || "").trim().toLowerCase() !== serviceTypeKey
                ) {
                    return;
                }
                (items || []).forEach((item) => {
                    const pid = normalizeId(item?.ProjectID ?? item?.projectid);
                    if (pid) ids.add(pid);
                });
            });
        });
        (matchedProjects || []).forEach((p) => {
            const ts = String(p?.TypeService || "").trim().toLowerCase();
            if (serviceTypeKey && ts && ts !== serviceTypeKey) return;
            const pid = normalizeId(p?._id ?? p?.ProjectID);
            if (pid) ids.add(pid);
        });
        return ids;
    }, [softwarePersons, filteredQuotation, matchedProjects, serviceTypeKey]);

    // Group persons by manager and their operations
    const groupPersonsByManager = () => {
        const managers = softwarePersons.filter(p => p.roles?.[0] === "Project manager");
        const operations = softwarePersons.filter(p => p.roles?.[0] === "Operations");

        const grouped = managers.map(manager => {
            // Find operations persons that belong to this manager (same ProjectID)
            const managerOperations = operations.filter(op =>
                normalizeId(op.ProjectID) === normalizeId(manager.ProjectID)
            );

            return {
                manager,
                operations: managerOperations
            };
        });

        return grouped;
    };

    // Get person display name with manager-specific numbering
    const getPersonDisplayName = (person) => {
        const serviceType = person.typeService || "Software";
        const role = person.roles?.[0] || "";

        if (role === "Project manager") {
            // Find manager number
            const managers = softwarePersons.filter(p => p.roles?.[0] === "Project manager");
            const managerNumber = managers.findIndex(p =>
                normalizeId(p.personId) === normalizeId(person.personId)
            ) + 1;
            return `${serviceType} Manager ${managerNumber}`;
        } else if (role === "Operations") {
            // Find which manager this operations person belongs to and their number within that manager
            const grouped = groupPersonsByManager();

            for (const group of grouped) {
                const operationIndex = group.operations.findIndex(op =>
                    normalizeId(op.personId) === normalizeId(person.personId)
                );
                if (operationIndex !== -1) {
                    return `${serviceType} Employee ${operationIndex + 1}`;
                }
            }

            // Fallback if no manager found
            return `${serviceType} Employee`;
        } else {
            return `${serviceType} ${role}`;
        }
    };

    const getTaskOwnerId = (task) => normalizeId(task?.taskownerid ?? task?.taskOwnerId);
    const getTaskProjectId = (task) => normalizeId(task?.projectid ?? task?.projectId);
    const taskAmount = (task) => parseAmountNumber(task?.value ?? task?.expenses);
    const personMatchesTaskType = (person, task) => {
        const pType = String(person?.typeService || "").trim().toLowerCase();
        const tType = String(task?.typeservice || task?.typeService || "").trim().toLowerCase();
        if (!pType) return true;
        if (!tType) return true;
        return tType === pType;
    };

    // Match description tasks the same way AllServiceTable builds Associated Cost
    const getAllPersonsTasks = () => {
        const taskList = Array.isArray(tasks) ? tasks : [];
        return softwarePersons.flatMap((person) => {
            const personId = normalizeId(person.personId);
            if (!personId) return [];
            return taskList.filter((task) => {
                if (getTaskOwnerId(task) !== personId) return false;
                const taskProjectId = getTaskProjectId(task);
                if (!taskProjectId) return false;
                const personProjectId = normalizeId(person.ProjectID);
                const projectOk =
                    (personProjectId && taskProjectId === personProjectId) ||
                    (serviceProjectIds.size > 0 && serviceProjectIds.has(taskProjectId));
                if (!projectOk) return false;
                return personMatchesTaskType(person, task);
            });
        });
    };

    const allPersonsTasks = getAllPersonsTasks();

    const canonicalDesc = (d) =>
        canonicalInstallationAssociatedTaskDescription(d == null ? '' : String(d), currency);

    const labelSet = new Set();
    allPersonsTasks.forEach((task) => {
        const c = canonicalDesc(task.description ?? task.Description);
        if (c) labelSet.add(c);
    });
    const labels = [...labelSet];
    const trainingLabel = labels.find((x) => isTrainingExpenseDisplayRow({ description: x }));
    const allDescriptions = trainingLabel
        ? [
            trainingLabel,
            ...labels.filter((x) => x !== trainingLabel).sort((a, b) => a.localeCompare(b)),
        ]
        : labels.sort((a, b) => a.localeCompare(b));

    // Helper function to get total for a specific description and person
    const getDescriptionTotalForPerson = (description, personId) => {
        const oid = normalizeId(personId);
        const matchingTasks = allPersonsTasks.filter(
            (task) =>
                getTaskOwnerId(task) === oid &&
                canonicalDesc(task.description ?? task.Description) === description
        );
        if (isTrainingExpenseDisplayRow({ description })) {
            return sumInstallationAssociatedTrainingValues(matchingTasks);
        }
        return matchingTasks.reduce((sum, task) => sum + taskAmount(task), 0);
    };

    // Calculate total cost for each person
    const getPersonTotalCost = (personId) => {
        const oid = normalizeId(personId);
        return allPersonsTasks
            .filter((task) => getTaskOwnerId(task) === oid)
            .reduce((sum, task) => sum + taskAmount(task), 0);
    };

    // Show table whenever this service has manager/ops rows. Hide only when no persons.
    // Other-expense rows may be empty (efforts only) — still show the table shell.
    if (!softwarePersons.length) {
        return null;
    }

    return (
        <div ref={AssociatedRef} className='pt-5 pl-2'>
            <div className="text-lg font-semibold pb-2">
                <h2>Associated Costs: {softwarePersons[0]?.typeService}</h2>
            </div>

            {/* Vertical Table for Multiple Users */}
            <div className="inline-block">
                {/* Header Row */}
                <div className="flex font-semibold text-xs border-x">
                    <div className="w-40 bg-[#e8e8e8] p-3 flex border-b justify-start align-middle items-center">Task</div>
                    {softwarePersons.map((person, index) => (
                        <div key={index} className="w-40 bg-[#e8e8e8] border-b capitalize p-3  text-center flex justify-center align-middle items-center">
                            {getPersonDisplayName(person)}<br />
                        </div>
                    ))}
                </div>
                {/* Totals Row */}
                <div className="flex text-xs border-x">
                    <div className="w-40 p-3 flex justify-start align-middle border-b font-semibold items-center">Total ({currency})</div>
                    {softwarePersons.map((person, index) => (
                        <div key={index} className="w-40 p-3 text-center border-b font-semibold flex justify-center align-middle items-center">
                            {formatMoneyComma(getPersonTotalCost(person.personId))}
                        </div>
                    ))}
                </div>
                {/* Scrollable Descriptions */}
                <div className="max-h-60 narrow-scrollbar overflow-auto inline-block border-x">
                    {allDescriptions.map((description, index) => (
                        <div
                            key={index}
                            className={`flex text-xs `}
                        >
                            <div className="w-40 p-3 flex  border-b justify-start align-middle items-center">
                                {description}
                            </div>
                            {softwarePersons.map((person, personIndex) => {
                                const descriptionTotal = getDescriptionTotalForPerson(description, person.personId);
                                return (
                                    <div key={personIndex} className="w-40 p-3 border-b text-center flex justify-center align-middle items-center">
                                        {descriptionTotal > 0 ? formatMoneyComma(descriptionTotal) : '-'}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default SoftwareTypeServiceTable;
